/**
 * VignanLogin2
 * -------------------------------------------------------------------------
 * Vignan Student Portal — student landing + login (animated variant).
 * A copy of the VignanLogin1 structure, tuned for the student side with
 * rich-but-tasteful CSS-only animations.
 *
 *   - Hero + embedded login, stats band, student tools, how-it-works, footer
 *   - Real GraphQL `login` mutation via react-apollo
 *   - Show / Hide password, validation, loading state, error handling
 *   - On success it redirects to /student/home
 *
 * Styling lives in ./vignanlogin2.scss (scoped under .vg-portal2).
 * All animations are pure CSS and honor prefers-reduced-motion.
 * -------------------------------------------------------------------------
 */

import React, { Component } from "react";
import { Button, Spinner, Alert } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";

import vignanLogo from "../../images/vignan_logo.png";
import happyStudents from "../../images/happy-students.png";
import "./vignanlogin2.scss";

const LOGIN_USER = gql`
  mutation($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      refreshToken
      user {
        username
        userlevel
        email
        mobile
        valid
        name
        institution_id
        institute_name
      }
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Validators                                                          */
/* ------------------------------------------------------------------ */

const validateUsername = (value = "") => {
  const v = value.trim();
  if (!v) return "Username is required";
  if (v.length < 3) return "Username must be at least 3 characters";
  return "";
};

const validatePassword = (value = "") => {
  if (!value) return "Password is required";
  if (value.length < 6) return "Password must be at least 6 characters";
  return "";
};

/* ------------------------------------------------------------------ */
/* Static content                                                      */
/* ------------------------------------------------------------------ */

const HIGHLIGHTS = [
  { icon: "fa-chalkboard-teacher", text: "Attend live & recorded classes", color: "red" },
  { icon: "fa-file-alt", text: "Practice & take online exams", color: "blue" },
  { icon: "fa-chart-line", text: "Track your performance & rank", color: "purple" },
  { icon: "fa-book-open", text: "Access study material anytime", color: "amber" },
];

const FEATURES = [
  { icon: "fa-chalkboard-teacher", title: "Live Classes", desc: "Join interactive live sessions or watch the recordings anytime.", color: "red" },
  { icon: "fa-file-alt", title: "Online Exams", desc: "Attempt scheduled, mock and practice tests from anywhere.", color: "blue" },
  { icon: "fa-book-open", title: "Study Material", desc: "Chapter-wise notes, short notes and revision material.", color: "purple" },
  { icon: "fa-chart-line", title: "Performance Analytics", desc: "Detailed reports on accuracy, time and improvement.", color: "amber" },
  { icon: "fa-file-pdf", title: "Previous Papers", desc: "Solve previous year papers with detailed solutions.", color: "red" },
  { icon: "fa-comments", title: "Doubt Clearing", desc: "Get your doubts resolved by mentors and faculty.", color: "blue" },
  { icon: "fa-video", title: "Video Lectures", desc: "Concept videos for every topic — learn at your own pace.", color: "purple" },
  { icon: "fa-trophy", title: "Rank Predictor", desc: "Estimate your rank and benchmark against your peers.", color: "amber" },
];

const STATS = [
  { icon: "fa-user-graduate", value: "2000+", label: "Active Students" },
  { icon: "fa-question-circle", value: "50K+", label: "Practice Questions" },
  { icon: "fa-play-circle", value: "1000+", label: "Video Lessons" },
  { icon: "fa-award", value: "95%", label: "Success Rate" },
];

const STEPS = [
  { icon: "fa-sign-in-alt", title: "Login", desc: "Sign in securely with your student credentials.", color: "red" },
  { icon: "fa-laptop-code", title: "Learn", desc: "Watch classes and read curated study material.", color: "blue" },
  { icon: "fa-pen-nib", title: "Practice", desc: "Attempt tests and sharpen your concepts.", color: "purple" },
  { icon: "fa-chart-pie", title: "Track", desc: "Analyse results and improve continuously.", color: "amber" },
];

/* ------------------------------------------------------------------ */
/* Small presentational components                                     */
/* ------------------------------------------------------------------ */

class FeatureCard extends Component {
  render() {
    const { icon, title, desc, color, index } = this.props;
    return (
      <div className="vg-feature vg-reveal" style={{ animationDelay: `${0.06 * index}s` }}>
        <span className={`vg-feature__icon vg-ic--${color}`}>
          <i className={`fas ${icon}`} />
        </span>
        <h3 className="vg-feature__title">{title}</h3>
        <p className="vg-feature__desc">{desc}</p>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

class VignanLogin2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      values: { username: "", password: "" },
      touched: {},
      showPassword: false,
      loading: false,
      submitError: "",
    };
  }

  componentDidMount() {
    if (Cookies.get("studenttoken")) {
      this.props.history.push("/student/home");
    }
  }

  getErrors() {
    const { values } = this.state;
    return {
      username: validateUsername(values.username),
      password: validatePassword(values.password),
    };
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      values: { ...prev.values, [name]: value },
      submitError: "",
    }));
  };

  handleBlur = (e) => {
    const { name } = e.target;
    this.setState((prev) => ({ touched: { ...prev.touched, [name]: true } }));
  };

  togglePassword = () =>
    this.setState((s) => ({ showPassword: !s.showPassword }));

  handleSubmit = (e) => {
    e.preventDefault();
    const errors = this.getErrors();
    const isValid = !errors.username && !errors.password;

    this.setState({ touched: { username: true, password: true } });
    if (!isValid) return;

    const { username, password } = this.state.values;
    this.setState({ loading: true, submitError: "" });

    this.login(username.trim(), password).catch((error) => {
      const message =
        (error.graphQLErrors &&
          error.graphQLErrors.map((x) => x.message).join(", ")) ||
        "Unable to login. Please check your credentials and try again.";
      this.setState({ submitError: message, loading: false });
    });
  };

  login = async (username, password) => {
    await this.props.login({
      variables: { username, password },
      update: (store, { data }) => {
        const { token, refreshToken, user } = data.login;

        Cookies.set("token", token);
        Cookies.set("studenttoken", token);
        Cookies.set("refreshtoken", refreshToken);
        Cookies.set("studentrefreshtoken", refreshToken);
        Cookies.set("username", user.username);
        Cookies.set("studentusername", user.name);
        Cookies.set("name", user.name);
        Cookies.set("email", user.email);
        Cookies.set("studentemail", user.email);
        Cookies.set("mobile", user.mobile);
        Cookies.set("role", "student");
        Cookies.set("userlevel", user.userlevel);
        Cookies.set("student_userlevel", user.userlevel);
        Cookies.set("user_valid", user.valid);
        Cookies.set("institution_id", user.institution_id);
        Cookies.set("institute_name", user.institute_name);
        Cookies.set("toggle", "wrapper");

        if (data.login) {
          this.props.history.push("/student/home");
        }
      },
    });
  };

  renderLoginCard() {
    const { values, touched, showPassword, loading, submitError } = this.state;
    const errors = this.getErrors();

    return (
      <div className="vg-card">
        <span className="vg-card__bar" aria-hidden="true" />
        <div className="vg-card__head">
          <h2 className="vg-card__title">
            Student <span>Login</span>
          </h2>
          <p className="vg-card__subtitle">Sign in to continue to your account</p>
        </div>

        <form className="vg-form" onSubmit={this.handleSubmit} noValidate>
          {submitError && (
            <Alert variant="danger" className="vg-alert" role="alert">
              <i className="fas fa-times-circle mr-2" />
              {submitError}
            </Alert>
          )}

          <div className="vg-field">
            <label htmlFor="vg2-username" className="vg-label">
              <i className="far fa-user vg-label__ic" /> Username / Email
            </label>
            <div className={`vg-input ${touched.username && errors.username ? "is-invalid" : ""}`}>
              <input
                id="vg2-username"
                className="vg-input__control"
                type="text"
                name="username"
                placeholder="Enter your username or email"
                autoComplete="username"
                value={values.username}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                aria-invalid={touched.username && errors.username ? "true" : "false"}
                aria-describedby={touched.username && errors.username ? "vg2-username-error" : undefined}
              />
            </div>
            {touched.username && errors.username && (
              <span className="vg-error" id="vg2-username-error" role="alert">
                <i className="fas fa-exclamation-circle" /> {errors.username}
              </span>
            )}
          </div>

          <div className="vg-field">
            <label htmlFor="vg2-password" className="vg-label">
              <i className="fas fa-lock vg-label__ic" /> Password
            </label>
            <div className={`vg-input ${touched.password && errors.password ? "is-invalid" : ""}`}>
              <input
                id="vg2-password"
                className="vg-input__control"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                value={values.password}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                aria-invalid={touched.password && errors.password ? "true" : "false"}
                aria-describedby={touched.password && errors.password ? "vg2-password-error" : undefined}
              />
              <button
                type="button"
                className="vg-input__toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={this.togglePassword}
              >
                <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
            {touched.password && errors.password && (
              <span className="vg-error" id="vg2-password-error" role="alert">
                <i className="fas fa-exclamation-circle" /> {errors.password}
              </span>
            )}
          </div>

          <Button type="submit" className="vg-submit" disabled={loading} block>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" /> Signing in…
              </>
            ) : (
              <>
                Login <i className="fas fa-arrow-right ml-2" />
              </>
            )}
          </Button>
        </form>

        <div className="vg-card__foot">
          Need help? <i className="fas fa-phone-alt" />{" "}
          <a href="tel:+919063148877">+91 906 314 8877</a>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="vg-portal2">
        {/* ---------- Hero ---------- */}
        <section className="vg-hero">
          <span className="vg-blob vg-blob--1" aria-hidden="true" />
          <span className="vg-blob vg-blob--2" aria-hidden="true" />
          <span className="vg-blob vg-blob--3" aria-hidden="true" />

          <div className="vg-container vg-hero__grid">
            <div className="vg-hero__intro">
              <img src={vignanLogo} alt="Vignan Junior College" className="vg-hero__logo" />
              <h1 className="vg-hero__title">
                Learn smarter. <span>Track better.</span>
              </h1>
              <p className="vg-hero__lead">
                Your complete student portal — live classes, online exams, study
                material and personalised analytics, all in one beautiful place.
              </p>
              <ul className="vg-hero__list">
                {HIGHLIGHTS.map((h, i) => (
                  <li key={h.text} className="vg-reveal" style={{ animationDelay: `${0.12 + 0.08 * i}s` }}>
                    <span className={`vg-hl-ic vg-ic--${h.color}`}>
                      <i className={`fas ${h.icon}`} />
                    </span>
                    {h.text}
                  </li>
                ))}
              </ul>
              <img src={happyStudents} alt="" aria-hidden="true" className="vg-hero__art" />
            </div>

            <div className="vg-hero__login">{this.renderLoginCard()}</div>
          </div>
        </section>

        {/* ---------- Stats band ---------- */}
        <section className="vg-statsband">
          <div className="vg-container vg-stats">
            {STATS.map((s, i) => (
              <div className="vg-stat vg-reveal" key={s.label} style={{ animationDelay: `${0.06 * i}s` }}>
                <i className={`fas ${s.icon} vg-stat__ic`} />
                <div className="vg-stat__value">{s.value}</div>
                <div className="vg-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- Student tools ---------- */}
        <section className="vg-section">
          <div className="vg-container">
            <h2 className="vg-heading">Everything you need to succeed</h2>
            <p className="vg-subheading">Your complete learning toolkit, available 24/7</p>
            <div className="vg-grid-4">
              {FEATURES.map((f, i) => (
                <FeatureCard key={f.title} index={i} {...f} />
              ))}
            </div>
          </div>
        </section>

        {/* ---------- How it works ---------- */}
        <section className="vg-section">
          <div className="vg-container">
            <h2 className="vg-heading">How it works</h2>
            <p className="vg-subheading">Get started in four simple steps</p>
            <div className="vg-steps">
              {STEPS.map((s, i) => (
                <div
                  className={`vg-step vg-step--${s.color} vg-reveal`}
                  key={s.title}
                  style={{ animationDelay: `${0.08 * i}s` }}
                >
                  <span className="vg-step__no">{i + 1}</span>
                  <span className="vg-step__ic"><i className={`fas ${s.icon}`} /></span>
                  <h3 className="vg-step__title">{s.title}</h3>
                  <p className="vg-step__desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Footer ---------- */}
        <footer className="vg-footer">
          <div className="vg-container">
            <div className="vg-footer__contacts">
              <span><i className="fas fa-phone-alt" /> +91 906 314 8877</span>
              <span><i className="fas fa-envelope" /> vignan.jrconnect@vignan.edu.in</span>
              <span><i className="fas fa-map-marker-alt" /> Venkatraya Nagar, Nizampet, Hyderabad, Telangana 500090</span>
            </div>
            <p className="vg-footer__copy">© 2026 Vignan Junior College. All rights reserved.</p>
            <p className="vg-footer__brand">A product of <strong>myLearningPlus</strong></p>
          </div>
        </footer>
      </div>
    );
  }
}

export default withRouter(
  graphql(LOGIN_USER, {
    name: "login",
  })(VignanLogin2)
);
